import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
type RevealProps = {
  children: React.ReactNode;
  /** Stagger index — 60ms per step. Used only for the hero entrance. */
  delayStep?: number;
  /** Reveal immediately on mount instead of on scroll into view. */
  onMount?: boolean;
  className?: string;
  as?: 'div' | 'section' | 'article' | 'li';
};
const EASE = [0.22, 0.61, 0.36, 1] as const;

/**
 * The site's only entrance animation: a 12px rise with a fade, played once.
 * Disabled entirely when the visitor prefers reduced motion.
 */
export function TextReveal({
  children,
  className = ''
}: {
  children: string;
  className?: string;
}) {
  const reduce = useReducedMotion();
  if (reduce) {
    return <span className={className} data-id="element-266" data-mp="apfdde0vwgv6">{children}</span>;
  }
  const words = children.split(' ');
  return <motion.span initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-80px'
  }} variants={{
    visible: {
      transition: {
        staggerChildren: 0.04
      }
    }
  }} className={className} data-id="element-267" data-mp="3j4z68bxn7x9p">
      {words.map((word, i) => <span key={i} className="inline-block overflow-hidden align-bottom" data-id="element-268" data-mp="iztpgkrh24iu" data-mpi={i}>
          <motion.span className="inline-block" variants={{
        hidden: {
          y: '100%',
          opacity: 0
        },
        visible: {
          y: 0,
          opacity: 1,
          transition: {
            duration: 0.5,
            ease: [0.22, 0.61, 0.36, 1]
          }
        }
      }} data-id="element-269" data-mp="nr3djgxdgzsm">
            {word}&nbsp;
          </motion.span>
        </span>)}
    </motion.span>;
}
export function Reveal({
  children,
  delayStep = 0,
  onMount = false,
  className,
  as = 'div'
}: RevealProps) {
  const reduce = useReducedMotion();
  const Tag = motion[as];
  if (reduce) {
    const Plain = as;
    return <Plain className={className} data-id="element-270" data-mp="1gez8ag3dshmr">{children}</Plain>;
  }
  const transition = {
    duration: 0.32,
    ease: EASE,
    delay: delayStep * 0.06
  };
  if (onMount) {
    return <Tag className={className} initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={transition} data-id="element-271" data-mp="3hh74df2k7248">
        {children}
      </Tag>;
  }
  return <Tag className={className} initial={{
    opacity: 0,
    y: 12
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={transition} data-id="element-272" data-mp="3qhltyqmk33dj">
      {children}
    </Tag>;
}